import { queryOptions } from '@tanstack/react-query';

import { getServicesQuery } from '@/api/booking/getServicesQuery';

import type { ServicesResponse, StandbyServicesQueryKey } from './types';

/**
 * `requestedBarberId` mirrors the standby stylist selection: `null` (Any
 * professional) scopes to the shop-level endpoint, a real id scopes to that
 * barber's endpoint. Standby has no cart, but a dedicated key namespace keeps
 * its cache independent of any booking-flow services query running elsewhere.
 *
 * Delegates to `getServicesQuery`'s `queryFn` rather than its `queryOptions`
 * wholesale — that fetcher ignores its `QueryFunctionContext` argument
 * (everything it needs is already closed over), so it is called directly
 * instead of typed against its own booking-scoped query key.
 */
export const standbyServicesQuery = (
  shopId: string,
  requestedBarberId: string | null,
) => {
  const bookingQueryFn = getServicesQuery(
    shopId,
    requestedBarberId ?? undefined,
  ).queryFn as () => Promise<ServicesResponse>;

  return queryOptions<
    ServicesResponse,
    Error,
    ServicesResponse,
    StandbyServicesQueryKey
  >({
    queryKey: ['standby', 'services', shopId, { barberId: requestedBarberId }],
    queryFn: bookingQueryFn,
    enabled: !!shopId,
    retry: false,
  });
};
